/**
 * Shared home-gym search component used by profile screens.
 * Encapsulates Google Places search, gym lookup, profile update, and remove logic.
 */

import { useState, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete';
import { MapPin, Trash2 } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { clearOnboardingState } from '@/lib/onboardingState';
import { markSeen } from '@/lib/tutorials';

const GOOGLE_MAPS_API_KEY = process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY || '';

export interface SelectedPlace {
  name: string;
  placeId: string;
  address: string;
  lat?: number;
  lng?: number;
}

interface HomeGymSearchProps {
  isOnboarding?: boolean;
  onComplete?: () => void;
}

export default function HomeGymSearch({ isOnboarding = false, onComplete }: HomeGymSearchProps) {
  const router = useRouter();
  const { user, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const autocompleteRef = useRef<ReturnType<typeof GooglePlacesAutocomplete> | null>(null);

  const handleSave = async () => {
    if (!selectedPlace) {
      setError('Please select your home gym from the list');
      return;
    }
    if (!user?.id) return;

    setLoading(true);
    setError('');

    try {
      const { data: existingGym } = await supabase
        .from('gyms')
        .select('id')
        .eq('place_id', selectedPlace.placeId)
        .maybeSingle();

      if (!existingGym?.id) {
        setError('This gym is not available in our database. Please contact support to add it.');
        setLoading(false);
        return;
      }

      const gymId = existingGym.id;

      const { error: membershipError } = await supabase
        .from('gym_memberships')
        .upsert(
          { gym_id: gymId, user_id: user.id, role: 'member', status: 'active' },
          { onConflict: 'gym_id,user_id', ignoreDuplicates: false },
        );

      if (membershipError) {
        setError(membershipError.message);
        setLoading(false);
        return;
      }

      const updateData: Record<string, unknown> = {
        gym_id: gymId,
        home_gym_id: gymId,
        home_gym_name: selectedPlace.name,
        home_gym_place_id: selectedPlace.placeId,
        home_gym_address: selectedPlace.address,
        home_gym_skipped: false,
      };

      if (selectedPlace.lat !== undefined) updateData.home_gym_lat = selectedPlace.lat;
      if (selectedPlace.lng !== undefined) updateData.home_gym_lng = selectedPlace.lng;

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (updateError) {
        setError(updateError.message);
        setLoading(false);
        return;
      }

      if (isOnboarding) {
        await supabase.from('profiles').update({ onboarding_completed: true }).eq('id', user.id);
        await clearOnboardingState(user.id);
        await AsyncStorage.setItem('onboarding:walkthrough_complete', 'true');
        await markSeen('home');
      }

      await refreshProfile();
      setLoading(false);

      if (onComplete) {
        onComplete();
      } else {
        router.replace(isOnboarding ? '/(tabs)' : '/(tabs)/profile');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');

    const { error: updateError } = await supabase
      .from('profiles')
      .update({
        gym_id: null,
        home_gym_name: null,
        home_gym_place_id: null,
        home_gym_address: null,
        home_gym_lat: null,
        home_gym_lng: null,
        home_gym_skipped: true,
      })
      .eq('id', user.id);

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    await refreshProfile();
    setLoading(false);
    router.push('/(tabs)/profile');
  };

  return (
    <KeyboardAvoidingView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        bounces={false}>
        <View style={styles.content}>
          <View style={styles.intro}>
            <MapPin size={32} color={theme.colors.accent} />
            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
              Search for your gym below
            </Text>
          </View>

          {error ? (
            <Text
              style={[
                styles.errorText,
                {
                  color: theme.colors.accent,
                  backgroundColor: theme.colors.surface,
                  borderColor: theme.colors.accent,
                },
              ]}>
              {error}
            </Text>
          ) : null}

          <View style={styles.form}>
            <View style={styles.inputContainer}>
              <Text style={[styles.label, { color: theme.colors.text }]}>Home Gym</Text>
              <GooglePlacesAutocomplete
                ref={autocompleteRef as any}
                placeholder="Search for your gym"
                fetchDetails
                onPress={(data, details = null) => {
                  if (!details) {
                    setError('Unable to fetch gym details. Please try selecting again.');
                    setSelectedPlace(null);
                    return;
                  }
                  setSelectedPlace({
                    name: details.name || data.structured_formatting.main_text,
                    placeId: data.place_id,
                    address: details.formatted_address || '',
                    lat: details.geometry?.location?.lat,
                    lng: details.geometry?.location?.lng,
                  });
                  setError('');
                }}
                query={{
                  key: GOOGLE_MAPS_API_KEY,
                  language: 'en',
                  types: 'establishment',
                  keyword: 'gym',
                }}
                styles={{
                  container: { flex: 0 },
                  textInputContainer: { backgroundColor: 'transparent' },
                  textInput: {
                    backgroundColor: theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.border,
                    borderRadius: 2,
                    paddingHorizontal: 16,
                    paddingVertical: 16,
                    fontSize: 16,
                    color: theme.colors.text,
                    height: 56,
                  },
                  listView: {
                    backgroundColor: theme.colors.surface,
                    borderWidth: 1,
                    borderColor: theme.colors.border,
                    borderRadius: 2,
                    marginTop: 8,
                  },
                  row: { backgroundColor: theme.colors.surface, paddingVertical: 16, paddingHorizontal: 16 },
                  separator: { height: 1, backgroundColor: theme.colors.border },
                  description: { color: theme.colors.text, fontSize: 15 },
                  poweredContainer: {
                    backgroundColor: theme.colors.surface,
                    borderTopWidth: 1,
                    borderTopColor: theme.colors.border,
                    paddingVertical: 8,
                  },
                }}
                textInputProps={{ placeholderTextColor: theme.colors.textTertiary, editable: !loading }}
                enablePoweredByContainer
                debounce={400}
              />
              {selectedPlace && (
                <View
                  style={[
                    styles.selectedPlaceContainer,
                    { backgroundColor: theme.colors.surface, borderColor: theme.colors.accent },
                  ]}>
                  <Text style={[styles.selectedPlaceTitle, { color: theme.colors.textSecondary }]}>Selected:</Text>
                  <Text style={[styles.selectedPlaceName, { color: theme.colors.text }]}>{selectedPlace.name}</Text>
                  <Text style={[styles.selectedPlaceAddress, { color: theme.colors.textSecondary }]}>{selectedPlace.address}</Text>
                </View>
              )}
            </View>

            <TouchableOpacity
              style={[
                styles.button,
                { backgroundColor: theme.colors.accent },
                (loading || !selectedPlace) && styles.buttonDisabled,
              ]}
              onPress={handleSave}
              disabled={loading || !selectedPlace}>
              {loading ? (
                <ActivityIndicator color={theme.colors.accentForeground} />
              ) : (
                <Text style={[styles.buttonText, { color: theme.colors.accentForeground }]}>Submit</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.removeButton, { borderColor: theme.colors.border }]}
              onPress={handleRemove}
              disabled={loading}>
              <Trash2 size={16} color={theme.colors.textSecondary} />
              <Text style={[styles.removeButtonText, { color: theme.colors.textSecondary }]}>Remove Home Gym</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingTop: 40, paddingBottom: 32 },
  content: { flex: 1, paddingHorizontal: 20 },
  intro: { alignItems: 'center', marginBottom: 24, gap: 12 },
  subtitle: { fontSize: 16, textAlign: 'center' },
  form: { gap: 20 },
  inputContainer: { gap: 8 },
  label: { fontSize: 14, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  selectedPlaceContainer: { borderWidth: 1, borderRadius: 2, padding: 16, marginTop: 12 },
  selectedPlaceTitle: { fontSize: 12, marginBottom: 4 },
  selectedPlaceName: { fontSize: 16, fontWeight: '600', marginBottom: 4 },
  selectedPlaceAddress: { fontSize: 14 },
  button: { paddingVertical: 18, borderRadius: 2, alignItems: 'center', marginTop: 16 },
  buttonDisabled: { opacity: 0.6 },
  buttonText: { fontSize: 18, fontWeight: '700' },
  errorText: { fontSize: 14, textAlign: 'center', padding: 12, borderRadius: 2, borderWidth: 1, marginBottom: 16 },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 2,
    borderWidth: 1,
  },
  removeButtonText: { fontSize: 16, fontWeight: '600' },
});
